import React, { useMemo } from "react";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { Badge, Spinner } from "react-bootstrap";
import useFormattedDate from "hooks/useFormatDate";

const truncate = (value, limit = 80) => {
  const text = String(value || "");
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}...`;
};

const getStatusVariant = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "FAILED") return "danger";
  if (normalized === "PENDING") return "warning";
  if (normalized === "PROCESSING") return "info";
  return "success";
};

const formatRecipients = (item) => {
  const recipients = item?.to || item?.recipients || [];
  if (Array.isArray(recipients)) return recipients.join(", ");
  return String(recipients || "");
};

export default function OutgoingEmailsTable({
  emails,
  loading,
  page,
  pageSize,
  totalPages,
  totalElements,
  setPage,
  setPageSize,
}) {
  const { formatDateTime } = useFormattedDate();

  const columns = useMemo(
    () => [
      {
        accessorKey: "subject",
        header: "Subject",
        cell: ({ row }) => {
          const item = row.original || {};
          const subject = item.subject || item.title || "Untitled";
          return <span className="fw-semibold">{truncate(subject, 50)}</span>;
        },
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "recipients",
        header: "To",
        cell: ({ row }) => {
          const text = formatRecipients(row.original || {});
          return <span className="text-muted">{truncate(text, 50)}</span>;
        },
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "preview",
        header: "Preview",
        cell: ({ row }) => {
          const item = row.original || {};
          const body = item.body || item.content || item.rawBody || "";
          return <span className="text-700">{truncate(body, 90)}</span>;
        },
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const status = row.original?.status || row.original?.emailStatus || "SENT";
          return <Badge bg={getStatusVariant(status)}>{status}</Badge>;
        },
        meta: { headerProps: { className: "text-900 text-center" }, cellProps: { className: "text-center" } },
      },
      {
        accessorKey: "sentAt",
        header: "Sent At",
        cell: ({ row }) => {
          const sentAt = row.original?.sentAt || row.original?.createdAt;
          return <span className="text-muted">{formatDateTime(sentAt)}</span>;
        },
        meta: { headerProps: { className: "text-900" } },
      },
    ],
    [formatDateTime],
  );

  const table = useAdvanceTable({
    data: emails,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  if (loading && emails.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center py-4">
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap fs-12"
          tableProps={{
            bordered: true,
            striped: true,
            className: "mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages || 0}
            totalElements={totalElements || 0}
            onPrev={
              page > 0 && totalElements > 0
                ? () => setPage((prev) => Math.max(prev - 1, 0))
                : undefined
            }
            onNext={
              page < totalPages - 1 && totalElements > 0
                ? () => setPage((prev) => prev + 1)
                : undefined
            }
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 20, 30, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>
    </>
  );
}
